'use client'

import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/utils'
import { Children } from 'react'

const badgeVariants = cva(
  'relative inline-flex shrink-0 items-center justify-center rounded-full font-medium whitespace-nowrap outline-none select-none',
  {
    variants: {
      size: {
        xs: 'min-h-6 gap-1 px-2 py-0.5 text-xs [&_svg]:size-4',
        sm: 'min-h-8 gap-1.5 px-2.5 py-1 text-sm [&_svg]:size-5',
        default: 'min-h-10 gap-2 px-3 py-2 [&_svg]:size-6',
        lg: 'min-h-12 gap-2.5 px-4 py-2.5 text-base [&_svg]:size-8',
        xl: 'min-h-14 gap-3 px-5 py-3 text-lg [&_svg]:size-10',
      },
      semanticRole: {
        primary: '',
        secondary: '',
        success: '',
        error: '',
        warning: '',
        info: '',
      },
      variant: {
        default: '',
        outlined: 'border-border bg-surface-2 border',
      },
    },
    compoundVariants: [
      {
        semanticRole: 'primary',
        variant: 'default',
        className: 'bg-primary/6 dark:bg-primary/10 text-primary',
      },
      {
        semanticRole: 'secondary',
        variant: 'default',
        className: 'bg-secondary/6 dark:bg-secondary/10 text-secondary',
      },
      {
        semanticRole: 'success',
        variant: 'default',
        className: 'bg-success/6 dark:bg-success/10 text-success',
      },
      {
        semanticRole: 'error',
        variant: 'default',
        className: 'bg-error/6 dark:bg-error/10 text-error',
      },
      {
        semanticRole: 'warning',
        variant: 'default',
        className: 'bg-warning/6 dark:bg-warning/10 text-warning',
      },
      {
        semanticRole: 'info',
        variant: 'default',
        className: 'bg-info/6 dark:bg-info/10 text-info',
      },
      {
        semanticRole: 'primary',
        variant: 'outlined',
        className: 'text-primary',
      },
      {
        semanticRole: 'secondary',
        variant: 'outlined',
        className: 'text-secondary bg-transparent',
      },
      {
        semanticRole: 'success',
        variant: 'outlined',
        className: 'border-success-border bg-surface-success text-success',
      },
      {
        semanticRole: 'error',
        variant: 'outlined',
        className: 'border-error-border bg-surface-error text-error',
      },
      {
        semanticRole: 'warning',
        variant: 'outlined',
        className: 'border-warning-border bg-surface-warning text-warning',
      },
      {
        semanticRole: 'info',
        variant: 'outlined',
        className: 'border-info-border bg-surface-info text-info',
      },
    ],
    defaultVariants: {
      variant: 'default',
      semanticRole: 'primary',
      // SUS: should we set default size here than in the code? The notification badge complicated this, so I had to remove it and workaround it.
    },
  },
)

interface Badge extends useRender.ComponentProps<'span'> {
  variant?: VariantProps<typeof badgeVariants>['variant']
  semanticRole?: VariantProps<typeof badgeVariants>['semanticRole']
  size?: VariantProps<typeof badgeVariants>['size']
  notificationCount?: number
  fixedColor?:
    | 'pink'
    | 'red'
    | 'orange'
    | 'yellow'
    | 'lightgreen'
    | 'green'
    | 'lightblue'
    | 'blue'
    | 'purple'
    | 'violet'
    | 'gray'
}

function Badge({
  className,
  variant,
  semanticRole,
  size,
  notificationCount,
  fixedColor,
  children,
  render,
  ...props
}: Badge) {
  const isNotification = notificationCount !== undefined
  const effectiveSemanticRole = isNotification ? 'error' : semanticRole

  let content = children
  let isDot = false

  if (isNotification) {
    if (notificationCount === 1) {
      isDot = true
      content = undefined
    } else if (notificationCount < 1000)
      content = <span className="element">{notificationCount}</span>
    else content = <span className="element">999+</span>
  } else {
    content = Children.map(children, (child) =>
      typeof child === 'string' ? (
        <span className="element">{child}</span>
      ) : (
        child
      ),
    )
  }

  const fixedColorMap: Record<NonNullable<Badge['fixedColor']>, string> = {
    pink: 'bg-pink/6 dark:bg-pink/10 text-pink',
    red: 'bg-red/6 dark:bg-red/10 text-red',
    orange: 'bg-orange/6 dark:bg-orange/10 text-orange',
    yellow: 'bg-yellow/6 dark:bg-yellow/10 text-yellow',
    lightgreen: 'bg-lightgreen/6 dark:bg-lightgreen/10 text-lightgreen',
    green: 'bg-green/6 dark:bg-green/10 text-green',
    lightblue: 'bg-lightblue/6 dark:bg-lightblue/10 text-lightblue',
    blue: 'bg-blue/6 dark:bg-blue/10 text-blue',
    purple: 'bg-purple/6 dark:bg-purple/10 text-purple',
    violet: 'bg-violet/6 dark:bg-violet/10 text-violet',
    gray: 'bg-gray/6 dark:bg-gray/10 text-gray',
  }

  const defaultProps = {
    className: cn(
      badgeVariants({
        variant,
        semanticRole: effectiveSemanticRole,
        size: isNotification ? undefined : (size ?? 'default'),
      }),
      fixedColor && fixedColorMap[fixedColor],
      isDot && 'size-2 p-0',
      isNotification && 'bg-error dark:bg-error absolute -top-1',
      // The "!" is necessary because twMerge deduplicates text-light-utility text-label-small for some reason.
      isNotification &&
        (isDot
          ? '-right-1'
          : '!text-light-utility text-label-small left-1/2 min-h-4 min-w-4 p-1 leading-none'),
      className,
    ),
    'data-slot': 'badge',
    children: content,
  }

  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(defaultProps, props),
    render,
  })
}

export { Badge }
